import * as React from "react";
import { css, ClassNames } from "@emotion/core";
import {
  Button,
  ButtonGroup,
  Tree,
  Text,
  EditableText,
  Tooltip,
  Position,
  Icon,
} from "@blueprintjs/core";
import { CheckViewAccess } from "context/";
import Layer from "models/Layer";

import { withStopPropagation, hexToRgb } from "utils/";
import {
  useDrawingState,
  useDrawingActions,
  useSinkState,
  useSinkActions,
  useAnnotationActions,
  useAnnotationState,
  useAnnotationSubscription,
} from "context/";

import ApiLayers from "api/layers";
import ApiAnnotations from "api/annotations";

import ColorPicker from "components/common/ColorPicker";

const LayerTreeNode = ({ layer }) => {
  const [name, setName] = React.useState(layer.label);

  React.useEffect(() => {
    setName(layer.label);
  }, [layer.label]);

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
      `}
    >
      {layer.isEditable ? (
        <CheckViewAccess fallback={<Text ellipsize>{layer.label}</Text>}>
          <EditableText
            selectAllOnFocus
            confirmOnEnterKey
            minWidth={200}
            maxLength={32}
            value={name}
            onChange={setName}
            onConfirm={(label) => {
              if (label !== layer.label) {
                ApiLayers.updateAnnotationLayer(layer.id, {
                  label,
                });
              }
            }}
          />
        </CheckViewAccess>
      ) : (
        <Text ellipsize>{layer.label}</Text>
      )}
    </div>
  );
};

const AnnotationTreeNode = ({ annotation, treeRef }) => {
  const { select } = useAnnotationActions();
  const { selected, focused } = useAnnotationSubscription(annotation);
  const { stopInspecting } = useSinkActions();
  const { sink } = useSinkState();

  const [name, setName] = React.useState(annotation.label);
  const prevSelected = React.useRef(selected);

  React.useEffect(() => {
    setName(annotation.label);
  }, [annotation.label]);

  React.useEffect(() => {
    const treeNode = treeRef.current?.getNodeContentElement(annotation.id);
    if (!treeNode) return;

    const { r, g, b } = hexToRgb(annotation.color);

    if (selected) {
      treeNode.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.7)`;
    } else {
      if (focused) {
        treeNode.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.3)`;
      } else {
        treeNode.style.backgroundColor = "transparent";
      }
    }

    if (selected && !prevSelected.current) {
      requestAnimationFrame(() => {
        treeRef.current
          ?.getNodeContentElement(annotation.id)
          ?.scrollIntoView({ block: "nearest" });
      });
    }
    prevSelected.current = selected;
  }, [annotation.id, annotation.color, selected, focused, treeRef]);

  const material = sink.materials.find(
    (material) => material.id === annotation.material_id
  );

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5px;
      `}
    >
      <CheckViewAccess
        fallback={
          <div
            css={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              padding-right: 4px;
            `}
          >
            {name}
            <Icon icon="tint" size={16} color={annotation.color} />
          </div>
        }
        checkLocked={annotation.source_id !== null}
      >
        <EditableText
          selectAllOnFocus
          confirmOnEnterKey
          minWidth={200}
          maxLength={32}
          value={name}
          onChange={setName}
          onConfirm={(label) => {
            if (label !== annotation.label) {
              ApiAnnotations.updateAnnotation(annotation.id, {
                label,
              });
            }
          }}
        />
        <div
          css={css`
            height: 100%;
            display: flex;
            align-items: center;
          `}
        >
          {annotation.user_measurements?.some(
            (measurement) => measurement.value === undefined
          ) && (
            <div
              css={css`
                padding: 0 0.5rem;
                display: flex;
                align-items: center;
              `}
            >
              <Icon icon="edit" size={14} />
            </div>
          )}
          <ButtonGroup>
            <ColorPicker
              color={annotation.color}
              onSelectColor={(color: string) =>
                ApiAnnotations.updateAnnotation(annotation.id, {
                  color,
                })
              }
            />
            {annotation.editable ? (
              <Button
                icon="trash"
                small
                onClick={withStopPropagation(() => {
                  stopInspecting();
                  annotation.api.archive();
                })}
              />
            ) : (
              <Tooltip
                content={material ? material.name : "No material selected"}
                position={Position.TOP}
                disabled={material === undefined}
              >
                <Button
                  icon="cube"
                  small
                  css={css`
                    & path {
                      fill: ${annotation.material_id
                        ? "var(--success)"
                        : "white"};
                      opacity: ${annotation.material_id ? 1 : 0.5};
                    }
                  `}
                  onClick={withStopPropagation(() => {
                    annotation.inspect(true);
                    select(annotation.id);
                  })}
                />
              </Tooltip>
            )}
          </ButtonGroup>
        </div>
      </CheckViewAccess>
    </div>
  );
};

const attach = (name, fn) => (list) =>
  list.map((list) => ({ ...list, [name]: fn }));

const toggleAllVisible = (layers) => {
  const visible = [];
  const invisible = [];
  const size = layers.length;

  for (let i = 0; i < size; i += 1) {
    const layer = layers[i];

    if (layer.visible) {
      visible.push(layer);
    } else {
      invisible.push(layer);
    }
  }

  if (invisible.length === 0) {
    visible.forEach((layer) => layer.setVisible(false));
  } else {
    invisible.forEach((layer) => layer.setVisible(true));
  }
};

/* TODO: split this into a {tree: { with tree data } and model: { with a reference to the model }} */
const LayerTree = ({ layers, isExpanded, toggle, collapse, expand }) => {
  const { isSelected } = useAnnotationState();
  const { focus, unfocus, select, deselect, subscribe, unsubscribe } =
    useAnnotationActions();
  const { mode: drawMode, activeDrawingLayer } = useDrawingState();
  const { setActiveDrawingLayer, clearActiveDrawingLayer } =
    useDrawingActions();

  const { inspectorModel, allowAnnotationArrowKeyNavigation } = useSinkState();
  const { stopInspecting, setAllowAnnotationArrowKeyNavigation } =
    useSinkActions();

  const visibleLayers = layers.filter((layer) => layer.visible);
  const someVisible = visibleLayers.length > 0;
  const allVisible = visibleLayers.length === layers.length;

  React.useEffect(() => {
    const fns = [];

    for (const layer of layers) {
      for (const annotation of layer.annotations) {
        const fn = (type, value) => {
          if (type === "CHANGE_SELECTED" && value) {
            expand(layer.id);
          }
        };

        fns.push({ id: annotation.id, fn });

        subscribe(annotation.id, fn);
      }
    }
    return () => fns.forEach(({ id, fn }) => unsubscribe(id, fn));
  }, [layers]);

  React.useEffect(() => {
    if (
      inspectorModel &&
      inspectorModel.class.name === "Annotation" &&
      allowAnnotationArrowKeyNavigation
    ) {
      const handler = (event) => {
        if (event.key === "ArrowDown") {
          const index = inspectorModel.layer.annotations.findIndex(
            ({ id }) => id === inspectorModel.id
          );

          const next = (index + 1) % inspectorModel.layer.annotations.length;

          const nextAnnotation = inspectorModel.layer.annotations[next];

          nextAnnotation.inspect();
          select(nextAnnotation.id);
        }

        if (event.key === "ArrowUp") {
          const index = inspectorModel.layer.annotations.findIndex(
            ({ id }) => id === inspectorModel.id
          );

          const previous =
            (index - 1 + inspectorModel.layer.annotations.length) %
            inspectorModel.layer.annotations.length;

          const previousAnnotation = inspectorModel.layer.annotations[previous];

          previousAnnotation.inspect();
          select(previousAnnotation.id);
        }
      };

      document.addEventListener("keydown", handler);

      return () => {
        document.removeEventListener("keydown", handler);
      };
    }
  }, [inspectorModel, allowAnnotationArrowKeyNavigation]);

  const allLayers = {
    id: "root",
    hasCaret: false,
    label: (
      <div
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <Text ellipsize>All Layers</Text>
      </div>
    ),
    secondaryLabel: (
      <ButtonGroup>
        <Button
          css={css`
            opacity: ${allVisible ? 1 : 0.3};
            & path {
              fill: white;
            }
          `}
          icon={someVisible ? "eye-open" : "eye-off"}
          small
          onClick={() => toggleAllVisible(layers)}
        />
      </ButtonGroup>
    ),
  };

  const treeRef = React.useRef(null);

  if (layers.length === 0) return null;

  return (
    <ClassNames>
      {({ css }) => (
        <Tree
          ref={treeRef}
          contents={[
            allLayers /* start with the general All Layers layer */,
            ...layers.map((layer) => {
              const layerColor =
                (layer.render_properties && layer.render_properties.color) ||
                "currentColor";

              return {
                ...layer,
                model: layer,
                label: <LayerTreeNode layer={layer} />,
                secondaryLabel: (
                  <div
                    css={css`
                      height: 100%;
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <CheckViewAccess fallback={null} checkLocked={true}>
                      {layer.annotations.some((annotation) => {
                        if (annotation.user_measurements === null) {
                          return false;
                        }

                        return annotation.user_measurements.some(
                          (measurement) => measurement.value === undefined
                        );
                      }) && (
                        <div
                          css={css`
                            padding: 0 0.5rem;
                            display: flex;
                            align-items: center;
                          `}
                        >
                          <Icon icon="edit" size={14} />
                        </div>
                      )}
                    </CheckViewAccess>
                    <ButtonGroup>
                      {Layer.editableTypes.includes(layer.type) && (
                        <CheckViewAccess
                          fallback={null}
                          checkLocked={layer.source_type !== null}
                        >
                          <ColorPicker
                            color={layerColor}
                            onSelectColor={(color) => {
                              setActiveDrawingLayer(layer);
                              ApiLayers.updateAnnotationLayer(layer.id, {
                                color,
                              });
                              layer.annotations.forEach((annotation) => {
                                ApiAnnotations.updateAnnotation(annotation.id, {
                                  color: color,
                                });
                              });
                            }}
                          />
                          {layer.isEditable && (
                            <Button
                              icon="trash"
                              small
                              onClick={withStopPropagation((event) => {
                                ApiLayers.archiveAnnotationLayer(layer.id);
                                clearActiveDrawingLayer(layer);
                                stopInspecting();
                              })}
                            />
                          )}
                        </CheckViewAccess>
                      )}
                      <Button
                        icon={layer.visible ? "eye-open" : "eye-off"}
                        small
                        css={css`
                          opacity: ${layer.visible ? 1 : 0.3};
                          & path {
                            fill: white;
                          }
                        `}
                        onClick={withStopPropagation(layer.toggleVisible)}
                      />
                    </ButtonGroup>
                  </div>
                ),
                hasCaret:
                  layer.type === "annotation" && layer.annotations.length > 0,
                isExpanded:
                  isExpanded(layer.id) && layer.annotations.length > 0,
                childNodes: attach(
                  "expand",
                  expand
                )(
                  Object.values(layer.annotations).map((annotation) => ({
                    ...annotation,
                    model: annotation,
                    layer,
                    isSelected: annotation.isSelected,
                    className: css`
                      & > .bp4-tree-node-content:hover {
                        background-color: transparent;
                      }
                    `,
                    label: (
                      <AnnotationTreeNode
                        annotation={annotation}
                        treeRef={treeRef}
                      />
                    ),
                  }))
                ),
              };
            }),
          ]}
          onNodeClick={(node) => {
            if (node.id === "root") return;

            /* TODO: clean up this mess */
            if (node.class.name === "Annotation") {
              if (isSelected(node.id)) return;

              if (node.editable && activeDrawingLayer !== node.layer) {
                setActiveDrawingLayer(node.layer);
              }

              setAllowAnnotationArrowKeyNavigation(true);
              node.inspect();
              select(node.id);
              return;
            }

            if (node.class.name === "Layer") {
              if (node.type === "annotation") {
                if (!isExpanded(node.id)) {
                  expand(node.id);
                }
                if (node.editable && activeDrawingLayer !== node.model) {
                  setActiveDrawingLayer(node.model);
                }

                node.inspect();
                deselect();
                return;
              }
            }

            toggle(node.id);
          }}
          onNodeCollapse={(node) => {
            stopInspecting();
            collapse(node.id);
          }}
          onNodeExpand={(node) => expand(node.id)}
          onNodeMouseEnter={(node) => {
            if (node.class?.name === "Annotation") {
              focus(node.id);
            }
          }}
          onNodeMouseLeave={(node) => {
            if (node.class?.name === "Annotation") {
              unfocus(node.id);
            }
          }}
        />
      )}
    </ClassNames>
  );
};

export default LayerTree;
